import { z } from 'zod'

/**
 * Account-password policy shared by every signup surface (normal register and
 * social-signup completion). Mirrors the API-side rules on
 * CompleteSocialRegistrationDto.password / AddClientDto.password2 — keep both
 * in sync when changing anything here.
 */
export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 128

/**
 * An INSTANCE's administrator password — as opposed to the account password
 * above — may contain visible ASCII only: letters, digits and these 32
 * symbols. No space, no accent, nothing outside ASCII.
 *
 * Mirrors the API's `IsVmPassword` (src/vm/dto/vm-password.ts in mw-api-v2),
 * which refuses anything else on every route that takes one. The reasons are
 * the guest's, not ours: resetting a password through the guest agent later
 * fails for any non-ASCII character (a Proxmox limitation), the browser
 * console types keys through the guest's keyboard layout, and a space is
 * invisible. Every symbol is safe — the platform hands the password to the
 * guest as data.
 */
export const VM_PASSWORD_SYMBOLS = '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~'

/** The characters an instance password may not contain, each once, in the order typed. */
export function vmPasswordDisallowedCharacters(pwd: string): string[] {
  const disallowed: string[] = []
  // Iterates code points, so an emoji is one character, not two halves.
  for (const ch of pwd) {
    const code = ch.codePointAt(0) ?? 0
    const visibleAscii = code >= 0x21 && code <= 0x7e
    if (!visibleAscii && !disallowed.includes(ch)) disallowed.push(ch)
  }
  return disallowed
}

export function isVmPasswordCharsetValid(pwd: string): boolean {
  return vmPasswordDisallowedCharacters(pwd).length === 0
}

/** Whether `pwd` has one of the 32 symbols — the "special character" of an instance password. */
export function hasVmPasswordSymbol(pwd: string): boolean {
  return [...pwd].some(ch => VM_PASSWORD_SYMBOLS.includes(ch))
}

/** What a refused character is called when showing it would show nothing. */
export interface DisallowedCharacterNames {
  /** Any space separator: the space bar, a no-break space pasted from a document. */
  space: string
  /** A character with no glyph — tab, line break, zero-width space — by code point. */
  invisible: (codePoint: string) => string
}

/** Holds a lone combining mark up so it is seen, instead of landing on the comma before it. */
const DOTTED_CIRCLE = String.fromCodePoint(0x25cc)

/**
 * The refused characters of `pwd` as a form lists them: each one itself when
 * it can be seen, a word when it cannot, each name once, in the order typed.
 * Printed as itself, a zero-width space or a tab read "Not allowed: " and then
 * nothing — and they arrive by pasting, exactly when nobody knows they are
 * there.
 */
export function describeDisallowedCharacters(
  pwd: string,
  names: DisallowedCharacterNames,
): string {
  const labels: string[] = []
  for (const ch of vmPasswordDisallowedCharacters(pwd)) {
    const kind = characterKind(ch)
    const label = kind === 'space'
      ? names.space
      : kind === 'invisible'
        ? names.invisible(codePointLabel(ch))
        : kind === 'mark' ? DOTTED_CIRCLE + ch : ch
    if (!labels.includes(label)) labels.push(label)
  }
  return labels.join(', ')
}

/**
 * Whether `pwd` holds a character nobody could have seen typing it — which is
 * how a form knows to say "these come from pasting; type it instead" rather
 * than leave a customer staring at a password they believe is clean.
 */
export function hasInvisibleCharacters(pwd: string): boolean {
  return vmPasswordDisallowedCharacters(pwd).some(ch => characterKind(ch) === 'invisible')
}

function characterKind(ch: string): 'space' | 'invisible' | 'mark' | 'visible' {
  if (/\p{Zs}/u.test(ch)) return 'space'
  if (/[\p{C}\p{Zl}\p{Zp}]/u.test(ch)) return 'invisible'
  if (/\p{M}/u.test(ch)) return 'mark'
  return 'visible'
}

function codePointLabel(ch: string): string {
  const hex = (ch.codePointAt(0) ?? 0).toString(16).toUpperCase()
  return `U+${hex.padStart(4, '0')}`
}

/**
 * `account` is the billing-account password; `vm` an instance's administrator
 * password, which adds the character rule above and counts only those 32
 * symbols as "special" (an accent used to tick that box).
 */
export type PasswordPolicy = 'account' | 'vm'

export type PasswordRequirementKey
  = 'minLength' | 'lowercase' | 'uppercase' | 'number' | 'special'
    | 'allowedCharacters'

const REQUIREMENT_TESTS: Record<
  Exclude<PasswordRequirementKey, 'allowedCharacters'>,
  (pwd: string) => boolean
> = {
  minLength: pwd => pwd.length >= PASSWORD_MIN_LENGTH,
  lowercase: pwd => /[a-z]/.test(pwd),
  uppercase: pwd => /[A-Z]/.test(pwd),
  number: pwd => /\d/.test(pwd),
  special: pwd => /[^A-Za-z0-9]/.test(pwd),
}

export const PASSWORD_REQUIREMENT_KEYS
  = Object.keys(REQUIREMENT_TESTS) as PasswordRequirementKey[]

const VM_REQUIREMENT_KEYS: PasswordRequirementKey[] = [
  ...PASSWORD_REQUIREMENT_KEYS,
  'allowedCharacters',
]

function requirementMet(
  key: PasswordRequirementKey,
  pwd: string,
  policy: PasswordPolicy,
): boolean {
  // Nothing typed has not met it yet, like every other box on the list.
  if (key === 'allowedCharacters') return pwd.length > 0 && isVmPasswordCharsetValid(pwd)
  if (key === 'special' && policy === 'vm') return hasVmPasswordSymbol(pwd)
  return REQUIREMENT_TESTS[key](pwd)
}

export interface PasswordRequirement {
  key: PasswordRequirementKey
  met: boolean
}

export function passwordRequirements(
  pwd: string,
  policy: PasswordPolicy = 'account',
): PasswordRequirement[] {
  const keys = policy === 'vm' ? VM_REQUIREMENT_KEYS : PASSWORD_REQUIREMENT_KEYS
  return keys.map(key => ({ key, met: requirementMet(key, pwd, policy) }))
}

export function isPasswordCompliant(
  pwd: string,
  policy: PasswordPolicy = 'account',
): boolean {
  return pwd.length <= PASSWORD_MAX_LENGTH
    && passwordRequirements(pwd, policy).every(requirement => requirement.met)
}

export type PasswordStrengthLevel = 'weak' | 'fair' | 'good' | 'strong'

export interface PasswordStrength {
  level: PasswordStrengthLevel
  /** 0–100, for the meter bar width. */
  percentage: number
}

/**
 * Heuristic strength: one point per requirement met, plus length bonuses at 12
 * and 16 characters — so "strong" needs every class AND a longer passphrase.
 */
export function passwordStrength(pwd: string): PasswordStrength {
  if (!pwd) return { level: 'weak', percentage: 0 }

  let score = passwordRequirements(pwd).filter(r => r.met).length
  if (pwd.length >= 12) score++
  if (pwd.length >= 16) score++

  if (score <= 3) return { level: 'weak', percentage: 25 }
  if (score <= 4) return { level: 'fair', percentage: 50 }
  if (score <= 5) return { level: 'good', percentage: 75 }
  return { level: 'strong', percentage: 100 }
}

/** Zod schema for a new account password, with translated messages. */
export function createPasswordSchema(t: (key: string) => string) {
  return z.string()
    .max(PASSWORD_MAX_LENGTH, t('auth.password.tooLong'))
    .refine(pwd => isPasswordCompliant(pwd), t('auth.password.invalid'))
}
